import React, { useEffect } from 'react';
import Wrapper from './styles';
import { useLanguageContext } from '../../context/language_context';
import { FaTree, FaPaintBrush, FaTools } from 'react-icons/fa';

const AboutPage = () => {
  const { t, isArabic } = useLanguageContext();

  useEffect(() => {
    document.title = 'Art & Bois | About Us';
  }, []);

  return (
    <Wrapper>
      {/* Hero Section */}
      <section className='hero-section'>
        <div className='hero-content'>
          <nav className='breadcrumb'>
            <a href='/'>{isArabic ? 'الرئيسية' : 'Home'}</a>
            <span>/</span>
            <span className='current'>{isArabic ? 'من نحن' : 'About Us'}</span>
          </nav>
          <h1 className='hero-title'>
            {isArabic ? (
              <>تصميم الراحة،<br />تحديد المساحات</>
            ) : (
              <>Crafting Comfort,<br />Defining Spaces</>
            )}
          </h1>
        </div>
      </section>

      {/* Our Story Section */}
      <section className='story-section'>
        <div className='story-container'>
          <div className='story-image-wrapper'>
            <div className='story-image'>
              <img 
                src='https://lh3.googleusercontent.com/aida-public/AB6AXuC2yRRoAhCYvs-sxPVxzVqblZz027lSktKgnVvXsPdBHJlc6L5KTQW4d1to-hiFA0l4Jb7iz_DD9xEsNjEep9C47L4T0gCVptK_H-xAN-sGbWzxbxF33nftbrL7wXdOnu2FXC97b96DMyPGLe7_HvH7QhEAEYgGjZmxPsxUAkoDgeIe_AwjlwQakw8fe3ymO33T5x4rAzZA1jIkw0fe4Q5T8bgz63Zx12bOScDq2sknuaUUvSFV7UlV7QqhLJZ8m8hI0BYosO3mctw' 
                alt={isArabic ? 'حرفي رئيسي يعمل على الخشب' : 'Master craftsman working on wood'}
              />
            </div>
            <div className='image-border'></div>
          </div>
          
          <div className='story-content'>
            <div className='accent-line'></div>
            <h2 className='section-title'>{isArabic ? 'قصتنا' : 'Our Story'}</h2>
            <div className='story-text'>
              <p>
                {isArabic ? (
                  <>في Art & Bois، نؤمن بأن كل قطعة أثاث تحكي قصة. تأسست على مبادئ التصميم الاسكندنافي - البساطة والوظيفة والجمال - قضينا العقد الماضي في الحصول على أفضل المواد المستدامة لإنشاء قطع خالدة للمنزل العصري.</>
                ) : (
                  <>At Art & Bois, we believe that every piece of furniture tells a story. Founded on the principles of Scandinavian design—simplicity, functionality, and beauty—we have spent the last decade sourcing the finest sustainable materials to create timeless pieces for the modern home.</>
                )}
              </p>
              <p>
                {isArabic ? (
                  <>مهمتنا هي مزج الحرفية التقليدية مع الجماليات المعاصرة، لضمان أن مساحة معيشتك ليست مجرد غرفة، بل ملاذًا للأناقة والراحة. نتعاون مع الحرفيين المحليين الذين يشاركوننا التفاني في الجودة، مما يضمن إتقان كل منحنى ومفصل باللمسة البشرية.</>
                ) : (
                  <>Our mission is to blend traditional craftsmanship with contemporary aesthetics, ensuring your living space is not just a room, but a sanctuary of style and comfort. We partner with local artisans who share our dedication to quality, ensuring that every curve and joint is perfected with human touch.</>
                )}
              </p>
            </div>
            
            <div className='stats'>
              <div className='stat-item'>
                <span className='stat-number'>10+</span>
                <span className='stat-label'>{isArabic ? 'سنوات خبرة' : 'Years Experience'}</span>
              </div>
              <div className='stat-item'>
                <span className='stat-number'>5k+</span>
                <span className='stat-label'>{isArabic ? 'منازل سعيدة' : 'Happy Homes'}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className='values-section'>
        <div className='values-container'>
          <div className='values-header'>
            <span className='values-subtitle'>{isArabic ? 'لماذا تختارنا' : 'Why Choose Us'}</span>
            <h3 className='values-title'>{isArabic ? 'قيمنا الأساسية' : 'Our Core Values'}</h3>
          </div>
          
          <div className='values-grid'>
            <div className='value-card'>
              <div className='value-icon'>
                <FaTree />
              </div>
              <h4 className='value-title'>{isArabic ? 'مصادر مستدامة' : 'Sustainable Sourcing'}</h4>
              <p className='value-text'>
                {isArabic ? 
                  'نعطي الأولوية للمواد الصديقة للبيئة وطرق الإنتاج الأخلاقية، مما يضمن أن بصمتنا خفيفة مثل تصميماتنا.' :
                  'We prioritize eco-friendly materials and ethical production methods, ensuring our footprint is as light as our designs.'
                }
              </p>
            </div>
            
            <div className='value-card'>
              <div className='value-icon'>
                <FaPaintBrush />
              </div>
              <h4 className='value-title'>{isArabic ? 'تصميم خالد' : 'Timeless Design'}</h4>
              <p className='value-text'>
                {isArabic ?
                  'الاتجاهات تأتي وتذهب، لكن الأناقة الحقيقية تدوم إلى الأبد. قطعنا مصممة لتكون محببة لأجيال قادمة.' :
                  'Trends come and go, but true style lasts forever. Our pieces are crafted to be cherished for generations to come.'
                }
              </p>
            </div>
            
            <div className='value-card'>
              <div className='value-icon'>
                <FaTools />
              </div>
              <h4 className='value-title'>{isArabic ? 'حرفية فنية' : 'Artisanal Craftsmanship'}</h4>
              <p className='value-text'>
                {isArabic ?
                  'كل عنصر يتم تشطيبه يدويًا من قبل حرفيين ماهرين يصبون شغفهم وخبرتهم في كل التفاصيل.' :
                  'Each item is hand-finished by master craftsmen who pour their passion and expertise into every detail.'
                }
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className='cta-section'>
        <div className='cta-pattern'></div>
        <div className='cta-content'>
          <h2 className='cta-title'>
            {isArabic ? 'اختبر المجموعة بنفسك' : 'Experience the collection for yourself'}
          </h2>
          <p className='cta-text'>
            {isArabic ?
              'قم بتحويل منزلك مع مجموعتنا المختارة من الأثاث الاسكندنافي. اكتشف المزيج المثالي من الشكل والوظيفة.' :
              'Transform your home with our curated selection of Scandinavian furniture. Discover the perfect blend of form and function.'
            }
          </p>
          <a href='/products' className='cta-button'>
            {isArabic ? 'استكشف قطعنا' : 'Explore Our Pieces'}
          </a>
        </div>
      </section>
    </Wrapper>
  );
};

export default AboutPage;
